import React, { useEffect, useRef, useState } from "react";

/**
 * Sticky section scroller for the Services page.
 * - Sticks just below the navbar (top-[72px]) once the user scrolls past it.
 * - Travels with the scroll for the whole page.
 * - Highlights the active pillar based on which section is in view.
 * - Smooth-scrolls to anchors with a small offset to clear the navbar + bar.
 *
 * Accepts an array of { id, title } items.
 */
export default function ServicesStickyNav({ items }) {
  const [active, setActive] = useState(items[0]?.id);
  const railRef = useRef(null);
  const buttonRefs = useRef({});

  // Track which section is in view.
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        // Pick the entry with the largest intersectionRatio that's intersecting.
        let best = null;
        entries.forEach((e) => {
          if (e.isIntersecting && (!best || e.intersectionRatio > best.intersectionRatio)) {
            best = e;
          }
        });
        if (best?.target?.id) setActive(best.target.id);
      },
      {
        // Top offset = navbar (64) + sticky bar (~52) + a little padding
        rootMargin: "-150px 0px -55% 0px",
        threshold: [0, 0.25, 0.5, 0.75, 1],
      }
    );
    items.forEach((it) => {
      const el = document.getElementById(it.id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, [items]);

  // Auto-scroll the chip rail to keep the active chip in view (mobile).
  useEffect(() => {
    const rail = railRef.current;
    const btn = buttonRefs.current[active];
    if (!rail || !btn) return;
    const railRect = rail.getBoundingClientRect();
    const btnRect = btn.getBoundingClientRect();
    if (btnRect.left < railRect.left || btnRect.right > railRect.right) {
      rail.scrollTo({
        left: btn.offsetLeft - rail.clientWidth / 2 + btn.clientWidth / 2,
        behavior: "smooth",
      });
    }
  }, [active]);

  const handleClick = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY - 120;
    window.scrollTo({ top, behavior: "smooth" });
    setActive(id);
  };

  return (
    <div
      className="sticky top-[64px] z-30 bg-ink/85 backdrop-blur-xl border-y border-white/10"
      data-testid="services-sticky-nav"
    >
      <div className="max-w-site mx-auto px-4 md:px-12 lg:px-16">
        <div
          ref={railRef}
          className="flex items-center gap-1 md:gap-2 overflow-x-auto no-scrollbar h-12"
        >
          {items.map((it, i) => {
            const isActive = active === it.id;
            return (
              <a
                key={it.id}
                ref={(node) => {
                  buttonRefs.current[it.id] = node;
                }}
                href={`#${it.id}`}
                onClick={(e) => handleClick(e, it.id)}
                data-testid={`services-sticky-${it.id}`}
                className={`group relative shrink-0 inline-flex items-center gap-2 px-4 md:px-5 py-2 rounded-full text-[13px] md:text-sm font-medium transition-colors whitespace-nowrap ${
                  isActive
                    ? "text-ink bg-voltage"
                    : "text-paper/70 hover:text-paper hover:bg-white/5"
                }`}
              >
                <span
                  className={`font-mono text-[10px] tracking-[0.18em] ${
                    isActive ? "text-ink/70" : "text-paper/40"
                  }`}
                >
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span>{it.title}</span>
              </a>
            );
          })}
        </div>
      </div>
    </div>
  );
}
